<script setup lang="ts">
import { storeToRefs } from "pinia";
import CompanionProfileSettingsModal from "~/components/Companion/ProfileSettingsModal.vue";

const props = defineProps<{
  curriculumName?: string;
  licenseStatusLabel?: string;
  shouldConfirmCurriculumChange?: boolean;
  isLicenseExpired?: boolean;
  transferLicenseAssignmentId?: number;
  onLicenseTransferred?: () => void | Promise<void>;
}>();

const companionStore = useCompanion();
const { selectedProfile } = storeToRefs(companionStore);

const hideBrokenImage = (event: Event) => {
  (event.target as HTMLImageElement).classList.add("hidden");
};

const openProfileSettings = () => {
  const { open, close } = useDialog({
    title: "Profile Settings",
    component: CompanionProfileSettingsModal,
    props: {
      curriculumName: props.curriculumName,
      licenseStatusLabel: props.licenseStatusLabel,
      shouldConfirmCurriculumChange: props.shouldConfirmCurriculumChange,
      isLicenseExpired: props.isLicenseExpired,
      transferLicenseAssignmentId: props.transferLicenseAssignmentId,
      onClose: async () => {
        await props.onLicenseTransferred?.();
        await close();
      },
    },
  });

  open();
};
</script>

<template>
  <div
    v-if="selectedProfile"
    class="flex items-center rounded border border-gray-200 bg-white p-1 shadow-sm transition-all hover:border-gray-300"
  >
    <div class="flex items-center gap-2.5 pl-1 pr-3">
      <div
        class="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100"
      >
        <span class="text-xs font-bold text-gray-400">
          {{ selectedProfile.name.charAt(0).toUpperCase() }}
        </span>
        <img
          v-if="selectedProfile.avatarHref"
          :src="selectedProfile.avatarHref"
          :alt="selectedProfile.name"
          class="absolute inset-0 h-full w-full rounded-full object-cover"
          @error="hideBrokenImage"
        />
      </div>
      <span
        class="text-sm font-semibold text-gray-900 leading-none truncate max-w-[120px]"
      >
        {{ selectedProfile.name }}
      </span>
    </div>
    <div class="h-4 w-px bg-gray-200"></div>

    <div class="flex items-center gap-1 border-gray-200 pl-2 pr-1">
      <button
        class="rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        @click="openProfileSettings"
      >
        Settings
      </button>
      <!-- Add a vertical divider -->
      <div class="h-4 w-px bg-gray-200"></div>
      <button
        class="rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        @click="companionStore.resetProfileSelection"
      >
        Change
      </button>
    </div>
  </div>
</template>
